import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da22mjbk8r5te5mqseqg";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾蜜罐字段：非空则不提交，并给出与正常一致的反馈
    const honeypot = (fd.get("website_alt") || "").toString().trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      setEmail("");
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, value.toString());
    });

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      const isSpam =
        serverMsg.includes("spam") || serverMsg.includes("form data is spam");
      const ok = res.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setFormError("");
        form.reset();
        setEmail("");
      } else {
        setStatus("error");
        setFormError(parsed?.meta?.message || parsed?.meta?.detail || "订阅失败，请稍后再试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试");
    }
  };

  const quickLinks = [
    { label: "热门榜单", to: "/#hot" },
    { label: "最新更新", to: "/#new" },
    { label: "编辑精选", to: "/#pick" },
    { label: "剧情解读", to: "/#stories" },
    { label: "关于我们", to: "/about" },
  ];

  return (
    <footer className="bg-gradient-to-b from-background-100 to-background-200 text-foreground-700">
      {/* 订阅区 */}
      <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:py-16">
        <div className="flex flex-col gap-8 rounded-3xl bg-background-50 p-6 md:flex-row md:items-center md:justify-between md:p-10">
          <div className="max-w-lg">
            <h2 className="font-display text-2xl text-primary-600 md:text-3xl">恋爱更新提醒</h2>
            <p className="mt-2 text-[15px] text-foreground-600">
              订阅后，热门作品的更新、编辑精选和心动番外会第一时间推送到你的邮箱，不错过任何脸红瞬间。
            </p>
          </div>
          <form
            data-readdy-form="taotao-newsletter"
            onSubmit={handleSubscribe}
            className="flex w-full max-w-md flex-col gap-3 sm:flex-row"
          >
            <label htmlFor="footer-email" className="sr-only">
              邮箱地址
            </label>
            <input
              id="footer-email"
              type="email"
              name="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="输入你的邮箱"
              className="flex-1 rounded-full border border-background-300/70 bg-background-50 px-5 py-3 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
            <input
              type="text"
              name="website_alt"
              className="anti-spam-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              立即订阅
            </button>
          </form>
          {status === "success" && (
            <p className="w-full max-w-md text-sm text-primary-600 sm:max-w-sm">
              订阅成功，欢迎来到桃桃漫画网，喜欢的故事马上就到～
            </p>
          )}
          {status === "error" && (
            <p className="w-full max-w-md text-sm text-secondary-600 sm:max-w-sm">{formError}</p>
          )}
        </div>
      </div>

      {/* 主链接区 */}
      <div className="mx-auto w-full max-w-[1400px] px-4 pb-12">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-background-50">
                <i className="ri-heart-3-fill text-base"></i>
              </div>
              <span className="font-display text-xl text-primary-600">桃桃漫画网</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              桃色剧情恋爱拉扯在线看，精选暧昧桥段与心动瞬间，专为喜欢感情线慢慢升温的你而准备。
            </p>
          </div>

          <div>
            <h3 className="text-[15px] font-bold text-foreground-950">热门栏目</h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              {categories.map((c) => (
                <li key={c.id}>
                  <Link to={`/category/${c.id}`} className="transition-colors hover:text-primary-600">
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[15px] font-bold text-foreground-950">快速导航</h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              {quickLinks.map((l) => (
                <li key={l.label}>
                  <a href={l.to} className="transition-colors hover:text-primary-600">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[15px] font-bold text-foreground-950">联系我们</h3>
            <ul className="mt-4 space-y-2.5 text-sm text-foreground-600">
              <li className="flex items-center gap-2">
                <i className="ri-mail-line text-primary-500"></i>
                hello@taotao-manga.example.com
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-wechat-line text-primary-500"></i>
                桃桃漫画网官方
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-time-line text-primary-500"></i>
                周一至周日 09:00-22:00
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-background-300/60">
        <div className="mx-auto flex w-full max-w-[1400px] flex-col items-center justify-between gap-3 px-4 py-5 text-sm text-foreground-500 sm:flex-row">
          <p>© 2026 桃桃漫画网 · 恋爱剧情在线看</p>
          <p className="flex items-center gap-2">
            收藏本站
            <i className="ri-bookmark-fill text-primary-400"></i>
            分享心动
            <i className="ri-heart-fill text-primary-400"></i>
          </p>
        </div>
      </div>
    </footer>
  );
}